import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Shield, Search, Plus, Check, Package, RotateCcw, ArrowLeftRight, AlertTriangle, Trash2, AlertCircle, Upload, Download, X, FileSpreadsheet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEDUCTION_LOCATIONS = [
  "Display Area",
  "Stock Room",
  "IF Restroom",
  "Room 1",
  "Warranty Reserve"
];

const BRANCHES = {
  "QC": "Quezon City",
  "PQ": "Parañaque",
  "CV": "Cavite",
  "PM": "Pampanga",
  "VZ": "Valenzuela",
  "OTG-A": "Orion On The Go - A",
  "OTG-B": "Orion On The Go - B",
  "OTG-C": "Orion On The Go - C"
};

const WARRANTY_TYPES = [
  { value: "RETURN_TO_STOCK", label: "Return to Stock", icon: RotateCcw, color: "bg-green-100 text-green-700", description: "Item returned, add back to inventory" },
  { value: "REPLACEMENT", label: "Replacement", icon: ArrowLeftRight, color: "bg-blue-100 text-blue-700", description: "Replace item (deduct replacement stock)" },
  { value: "DEFECTIVE", label: "Defective", icon: Trash2, color: "bg-red-100 text-red-700", description: "Mark as defective (no stock change)" },
  { value: "WARRANTY_CLAIM", label: "Warranty Claim", icon: Shield, color: "bg-orange-100 text-orange-700", description: "Track warranty claim status" }
];

export default function WarrantyTransactions() {
  const { user } = useAuth();
  const [selectedBranch, setSelectedBranch] = useState("QC");
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [transactionType, setTransactionType] = useState("RETURN_TO_STOCK");
  const [quantity, setQuantity] = useState(0.5);
  const [customerName, setCustomerName] = useState("");
  const [originalTracking, setOriginalTracking] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [warrantyDate, setWarrantyDate] = useState(""); // Warranty claim date
  const [deductionLocation, setDeductionLocation] = useState("Display Area"); // For WARRANTY_CLAIM/DEFECTIVE
  const [saving, setSaving] = useState(false);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [currentStock, setCurrentStock] = useState(null);
  
  // For replacement
  const [replacementSearch, setReplacementSearch] = useState("");
  const [replacementItem, setReplacementItem] = useState(null);
  const [filteredReplacements, setFilteredReplacements] = useState([]);
  const [replacementStock, setReplacementStock] = useState(null);
  
  // For delete
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [txToDelete, setTxToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);
  
  // For CSV/Excel Import
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);
  
  const searchRef = useRef(null);

  const fetchItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/item-master/list`);
      setItems(response.data.items || []);
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/warranty-transactions`, {
        params: { branch: selectedBranch, limit: 20 }
      });
      setRecentTransactions(response.data.transactions || []);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  }, [selectedBranch]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/warranty-transactions/stats`, {
        params: { branch: selectedBranch, days: 30 }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [selectedBranch]);

  const checkStock = useCallback(async (itemName, setter) => {
    if (!itemName) {
      setter(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/inventory/stock-check/${selectedBranch}/${encodeURIComponent(itemName)}`);
      setter(response.data.total_stock);
    } catch (error) {
      setter(0);
    }
  }, [selectedBranch]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    fetchTransactions();
    fetchStats();
  }, [fetchTransactions, fetchStats]);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredItems([]);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = items.filter(item =>
        item.item_name?.toLowerCase().includes(query)
      ).slice(0, 15);
      setFilteredItems(filtered);
    }
  }, [searchQuery, items]);

  useEffect(() => {
    if (!replacementSearch) {
      setFilteredReplacements([]);
    } else {
      const query = replacementSearch.toLowerCase();
      const filtered = items.filter(item =>
        item.item_name?.toLowerCase().includes(query)
      ).slice(0, 15);
      setFilteredReplacements(filtered);
    }
  }, [replacementSearch, items]);

  // Auto-set default deduction location based on transaction type
  // WARRANTY_CLAIM should NEVER deduct from Display Area by default — use Warranty Reserve
  useEffect(() => {
    if (transactionType === "WARRANTY_CLAIM") {
      setDeductionLocation("Warranty Reserve");
    } else if (transactionType === "DEFECTIVE") {
      setDeductionLocation("Display Area");
    }
  }, [transactionType]);

  useEffect(() => {
    if (selectedItem) {
      checkStock(selectedItem.item_name, setCurrentStock);
    }
  }, [selectedItem, selectedBranch, checkStock]);

  useEffect(() => {
    if (replacementItem) {
      checkStock(replacementItem.item_name, setReplacementStock);
    }
  }, [replacementItem, selectedBranch, checkStock]);

  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setSearchQuery(item.item_name);
    setFilteredItems([]);
  };

  const handleSelectReplacement = (item) => {
    setReplacementItem(item);
    setReplacementSearch(item.item_name);
    setFilteredReplacements([]);
  };

  const clearForm = () => {
    setSelectedItem(null);
    setSearchQuery("");
    setQuantity(0.5);
    setCustomerName("");
    setOriginalTracking("");
    setReason("");
    setNotes("");
    setWarrantyDate("");
    // Reset to type-appropriate default
    setDeductionLocation(transactionType === "WARRANTY_CLAIM" ? "Warranty Reserve" : "Display Area");
    setCurrentStock(null);
    setReplacementItem(null);
    setReplacementSearch("");
    setReplacementStock(null);
    setTimeout(() => searchRef.current?.focus(), 100);
  };

  const handleSubmit = async () => {
    if (!selectedItem) {
      toast.error("Pumili ng item");
      return;
    }
    if (quantity < 0.5) {
      toast.error("Quantity must be at least 0.5");
      return;
    }
    
    // For replacement, check if replacement item has enough stock
    if (transactionType === "REPLACEMENT") {
      if (!replacementItem) {
        toast.error("Pumili ng replacement item");
        return;
      }
      if (replacementStock !== null && quantity > replacementStock) {
        toast.error(`Hindi sapat ang replacement stock! Available: ${replacementStock}`);
        return;
      }
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/warranty-transactions`, {
        branch: selectedBranch,
        transaction_type: transactionType,
        item_id: selectedItem.id,
        item_name: selectedItem.item_name,
        item_code: selectedItem.item_code || "",
        quantity: quantity,
        customer_name: customerName,
        original_tracking: originalTracking,
        reason: reason,
        encoder: user?.name || user?.email?.split('@')[0] || "System",
        notes: notes,
        warranty_date: warrantyDate || null,
        location: deductionLocation || "Display Area",
        replacement_item_id: replacementItem?.id || "",
        replacement_item_name: replacementItem?.item_name || ""
      });

      toast.success(response.data.message);
      clearForm();
      await fetchTransactions();
      await fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers
  const openDeleteConfirm = (tx) => {
    setTxToDelete(tx);
    setDeleteReason("");
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!txToDelete) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.delete(`${API}/warranty-transactions/${txToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setShowDeleteConfirm(false);
      setTxToDelete(null);
      await fetchTransactions();
      await fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  // CSV/Excel Import handlers
  const downloadTemplate = () => {
    const headers = [
      "transaction_type", "item_name", "item_code", "quantity",
      "location", "customer_name", "original_tracking", "reason",
      "warranty_date", "replacement_item_name", "notes"
    ];
    const sampleRows = [
      ["WARRANTY_CLAIM", "Sample Item Name", "ITEM-001", 1, "Warranty Reserve", "Juan Dela Cruz", "TRK123456", "Defective bulb", "2026-01-15", "", "Customer claim - deducted from Warranty Reserve"],
      ["DEFECTIVE", "Another Item", "ITEM-002", 0.5, "Display Area", "", "", "Found defective", "", "", ""],
      ["REPLACEMENT", "Original Item", "ITEM-003", 1, "", "Maria Santos", "TRK789012", "Replacement under warranty", "2026-01-20", "Replacement Item", ""],
      ["RETURN_TO_STOCK", "Returned Item", "ITEM-004", 1, "Display Area", "", "", "Customer returned", "", "", ""]
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Warranty Template");
    XLSX.writeFile(wb, "warranty_import_template.xlsx");
  };

  const parseImportFile = async (file) => {
    if (!file) return;
    
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
      
      if (rows.length === 0) {
        toast.error("Walang data sa file");
        return;
      }

      // Normalize and validate each row
      const validTypes = ["WARRANTY_CLAIM", "DEFECTIVE", "REPLACEMENT", "RESHIP", "RETURN_TO_STOCK"];
      const itemNameMap = new Map(items.map(i => [i.item_name.toLowerCase().trim(), i]));
      
      const preview = rows.map((row, idx) => {
        // Be flexible with header names
        const getField = (...keys) => {
          for (const k of keys) {
            const val = row[k] ?? row[k.toLowerCase()] ?? row[k.toUpperCase()];
            if (val !== undefined && val !== "") return String(val).trim();
          }
          return "";
        };
        
        const txType = getField("transaction_type", "type", "Transaction Type").toUpperCase();
        const itemName = getField("item_name", "item", "Item Name");
        const itemCode = getField("item_code", "code", "Item Code");
        const qtyRaw = getField("quantity", "qty", "Quantity");
        // Smart default: WARRANTY_CLAIM defaults to Warranty Reserve, others to Display Area
        const locationRaw = getField("location", "Location");
        const location = locationRaw || (txType === "WARRANTY_CLAIM" ? "Warranty Reserve" : "Display Area");
        const customer = getField("customer_name", "customer", "Customer Name");
        const tracking = getField("original_tracking", "tracking", "Tracking");
        const reasonField = getField("reason", "Reason");
        const warrantyDateField = getField("warranty_date", "Warranty Date");
        const replacement = getField("replacement_item_name", "replacement", "Replacement Item");
        const notesField = getField("notes", "Notes");
        const quantity = parseFloat(qtyRaw) || 0;
        
        const errors = [];
        if (!validTypes.includes(txType)) {
          errors.push(`Invalid type "${txType}" (must be: ${validTypes.join(", ")})`);
        }
        if (!itemName) errors.push("Item name required");
        if (quantity <= 0) errors.push("Quantity must be > 0");
        
        const matchedItem = itemNameMap.get(itemName.toLowerCase().trim());
        if (itemName && !matchedItem) errors.push(`Item "${itemName}" not found in master list`);
        
        if (txType === "REPLACEMENT" && !replacement) {
          errors.push("Replacement item required for REPLACEMENT type");
        }
        
        return {
          row: idx + 1,
          transaction_type: txType,
          item_name: matchedItem?.item_name || itemName,
          item_id: matchedItem?.id || "",
          item_code: matchedItem?.item_code || itemCode,
          quantity,
          location,
          customer_name: customer,
          original_tracking: tracking,
          reason: reasonField,
          warranty_date: warrantyDateField,
          replacement_item_name: replacement,
          notes: notesField,
          errors,
          valid: errors.length === 0
        };
      });
      
      setImportPreview(preview);
      setImportResults(null);
    } catch (error) {
      console.error("Parse error:", error);
      toast.error(`Failed to parse file: ${error.message}`);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) parseImportFile(file);
  };

  const handleBulkImport = async () => {
    const validRows = importPreview.filter(r => r.valid);
    if (validRows.length === 0) {
      toast.error("Walang valid rows para i-import");
      return;
    }

    setImporting(true);
    try {
      const transactions = validRows.map(r => ({
        branch: selectedBranch,
        transaction_type: r.transaction_type,
        item_id: r.item_id,
        item_name: r.item_name,
        item_code: r.item_code,
        quantity: r.quantity,
        customer_name: r.customer_name,
        original_tracking: r.original_tracking,
        reason: r.reason,
        encoder: user?.name || user?.email?.split('@')[0] || "System",
        notes: r.notes,
        warranty_date: r.warranty_date || null,
        location: r.location || "Display Area",
        replacement_item_name: r.replacement_item_name
      }));
      
      const response = await axios.post(`${API}/warranty-transactions/batch`, {
        transactions
      });
      
      setImportResults(response.data);
      
      if (response.data.errors === 0) {
        toast.success(response.data.message);
      } else {
        toast.warning(`${response.data.saved} saved, ${response.data.errors} failed`);
      }
      
      await fetchTransactions();
      await fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportPreview([]);
    setImportResults(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const selectedTypeInfo = WARRANTY_TYPES.find(t => t.value === transactionType);
  const TypeIcon = selectedTypeInfo?.icon || Shield;

  return (
    <div className="min-h-screen bg-gray-50 p-4" data-testid="warranty-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-purple-600 flex items-center justify-center">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Warranty Transactions</h1>
              <p className="text-sm text-gray-500">Returns, Replacements, Defective Items</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowImportModal(true)}
              className="border-orange-500 text-orange-700 hover:bg-orange-50"
              data-testid="open-import-modal-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import CSV/Excel
            </Button>
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(BRANCHES).map(([code, name]) => (
                  <SelectItem key={code} value={code}>{code} - {name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="shadow-lg border-2 border-purple-200">
            <CardHeader className="bg-purple-50 pb-4">
              <CardTitle className="flex items-center gap-2 text-purple-800">
                <Plus className="h-5 w-5" />
                New Warranty Transaction
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {/* Transaction Type Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Transaction Type *</label>
                <div className="grid grid-cols-2 gap-2">
                  {WARRANTY_TYPES.map(type => (
                    <button
                      key={type.value}
                      data-testid={`warranty-type-${type.value}`}
                      onClick={() => setTransactionType(type.value)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        transactionType === type.value 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'border-gray-200 hover:border-purple-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`p-1.5 rounded ${type.color}`}>
                          <type.icon className="h-4 w-4" />
                        </span>
                        <span className="font-medium text-sm">{type.label}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Item Search */}
              <div className="space-y-2 relative">
                <label className="text-sm font-medium text-gray-700">
                  {transactionType === "RETURN_TO_STOCK" ? "Item to Return *" : "Original Item *"}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    ref={searchRef}
                    data-testid="warranty-item-search"
                    placeholder="Search item..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSelectedItem(null);
                    }}
                    className="pl-10 h-10"
                  />
                </div>
                
                {searchQuery && !selectedItem && filteredItems.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredItems.map((item) => (
                      <button
                        key={item.id}
                        data-testid={`warranty-item-option-${item.id}`}
                        onClick={() => handleSelectItem(item)}
                        className="w-full px-4 py-2 text-left hover:bg-purple-50 border-b last:border-b-0"
                      >
                        <span className="font-medium">{item.item_name}</span>
                      </button>
                    ))}
                  </div>
                )}

                {selectedItem && (
                  <div className={`p-3 rounded-lg border ${selectedTypeInfo?.color || 'bg-gray-50'}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-semibold">{selectedItem.item_name}</div>
                        <div className="text-sm">{selectedItem.category}</div>
                      </div>
                      <TypeIcon className="h-5 w-5" />
                    </div>
                  </div>
                )}
              </div>

              {/* Replacement Item (only for REPLACEMENT type) */}
              {transactionType === "REPLACEMENT" && (
                <div className="space-y-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <label className="text-sm font-medium text-blue-700">Replacement Item *</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      placeholder="Search replacement item..."
                      value={replacementSearch}
                      onChange={(e) => {
                        setReplacementSearch(e.target.value);
                        setReplacementItem(null);
                      }}
                      className="pl-10 h-10 bg-white"
                    />
                  </div>
                  
                  {replacementSearch && !replacementItem && filteredReplacements.length > 0 && (
                    <div className="bg-white border rounded-lg shadow-lg max-h-36 overflow-y-auto">
                      {filteredReplacements.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleSelectReplacement(item)}
                          className="w-full px-4 py-2 text-left hover:bg-blue-50 border-b last:border-b-0"
                        >
                          {item.item_name}
                        </button>
                      ))}
                    </div>
                  )}

                  {replacementItem && (
                    <div className="p-2 bg-white rounded border flex justify-between items-center">
                      <span className="font-medium">{replacementItem.item_name}</span>
                      {replacementStock !== null && (
                        <span className={`font-bold ${replacementStock <= 0 ? 'text-red-600' : 'text-green-600'}`}>
                          Stock: {replacementStock}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Quantity & Customer */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Quantity *</label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setQuantity(Math.max(0.5, quantity - 0.5))}
                      className="h-10 w-10 text-lg font-bold"
                      data-testid="qty-minus-btn"
                    >
                      -
                    </Button>
                    <Input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={quantity}
                      onChange={(e) => setQuantity(parseFloat(e.target.value) || 0.5)}
                      className="h-10 text-center font-bold flex-1"
                      data-testid="qty-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setQuantity(quantity + 0.5)}
                      className="h-10 w-10 text-lg font-bold"
                      data-testid="qty-plus-btn"
                    >
                      +
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">0.5 = single bulb from a 2-bulb set</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Customer Name</label>
                  <Input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Customer name"
                    className="h-10"
                  />
                </div>
              </div>

              {/* Warranty Date & Original Tracking */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Warranty Date</label>
                  <Input
                    type="date"
                    value={warrantyDate}
                    onChange={(e) => setWarrantyDate(e.target.value)}
                    className="h-10"
                    data-testid="warranty-date-input"
                  />
                  <p className="text-xs text-gray-500">Date ng warranty claim</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Original Tracking #</label>
                  <Input
                    value={originalTracking}
                    onChange={(e) => setOriginalTracking(e.target.value)}
                    placeholder="Original order tracking"
                    className="h-10"
                  />
                </div>
              </div>

              {/* Deduction Location - for WARRANTY_CLAIM and DEFECTIVE */}
              {(transactionType === "WARRANTY_CLAIM" || transactionType === "DEFECTIVE") && (
                <div className="space-y-2 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <label className="text-sm font-medium text-orange-800">
                    Deduct Stock From *
                  </label>
                  <Select value={deductionLocation} onValueChange={setDeductionLocation}>
                    <SelectTrigger className="h-10 bg-white" data-testid="deduction-location-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEDUCTION_LOCATIONS.map(loc => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-orange-700">
                    {transactionType === "WARRANTY_CLAIM"
                      ? "✅ Preferred source: Warranty Reserve. Kung walang stock dun, system AUTO-FALLBACK sa Display Area / Stock Room. Lahat ng auto-fallback ay naka-log sa Stock Audit."
                      : "Pumili kung saang location idi-deduct ang stock (default: Display Area). Auto-fallback active kung walang stock dun."}
                  </p>
                </div>
              )}

              {/* Reason */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Reason</label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason for return/replacement"
                  className="h-10"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Notes</label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes..."
                  className="h-10"
                />
              </div>

              {/* Warning for replacement stock */}
              {transactionType === "REPLACEMENT" && replacementStock !== null && quantity > replacementStock && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <AlertTriangle className="h-5 w-5" />
                  <span>Hindi sapat ang replacement stock! Available: <strong>{replacementStock}</strong></span>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  data-testid="warranty-submit-btn"
                  onClick={handleSubmit}
                  disabled={saving || !selectedItem || quantity < 0.5 || (transactionType === "REPLACEMENT" && (!replacementItem || (replacementStock !== null && quantity > replacementStock)))}
                  className="flex-1 h-12 text-lg bg-purple-600 hover:bg-purple-700"
                >
                  <Check className="h-5 w-5 mr-2" />
                  {saving ? "Saving..." : `Save ${selectedTypeInfo?.label || 'Transaction'}`}
                </Button>
                <Button variant="ghost" onClick={clearForm} className="h-12 px-4">
                  <RotateCcw className="h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Side - Stats & Recent */}
        <div className="space-y-4">
          {/* Stats */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Warranty Summary (30d)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats?.by_type?.map(t => {
                  const typeInfo = WARRANTY_TYPES.find(wt => wt.value === t.type);
                  return (
                    <div key={t.type} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${typeInfo?.color || 'bg-gray-100'}`}>
                        {typeInfo?.label || t.type}
                      </span>
                      <span className="font-bold">{t.qty} items</span>
                    </div>
                  );
                }) || <div className="text-gray-500 text-center py-4">No data</div>}
              </div>
              
              {stats?.stock_impact && (
                <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="p-2 bg-green-50 rounded">
                    <div className="font-bold text-green-700">+{stats.stock_impact.total_returned}</div>
                    <div className="text-green-600">Returned</div>
                  </div>
                  <div className="p-2 bg-blue-50 rounded">
                    <div className="font-bold text-blue-700">-{stats.stock_impact.total_replaced}</div>
                    <div className="text-blue-600">Replaced</div>
                  </div>
                  <div className="p-2 bg-red-50 rounded">
                    <div className="font-bold text-red-700">{stats.stock_impact.total_defective}</div>
                    <div className="text-red-600">Defective</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-72 overflow-y-auto">
                {recentTransactions.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">No transactions</div>
                ) : (
                  recentTransactions.map(tx => {
                    const typeInfo = WARRANTY_TYPES.find(t => t.value === tx.transaction_type);
                    return (
                      <div key={tx.id} className="px-4 py-2 border-b last:border-b-0 hover:bg-gray-50">
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate" title={tx.item_name}>{tx.item_name}</div>
                            <div className="text-xs space-y-0.5">
                              <span className={`px-1 rounded ${typeInfo?.color || 'bg-gray-100'}`}>
                                {typeInfo?.label || tx.transaction_type}
                              </span>
                              {tx.warranty_date && (
                                <div className="text-orange-600 font-medium">
                                  Warranty: {tx.warranty_date}
                                </div>
                              )}
                              {tx.customer_name && (
                                <div className="text-gray-500">{tx.customer_name}</div>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-2 flex-shrink-0">
                            <div>
                              <div className="font-bold text-base">×{tx.quantity}</div>
                              <div className="text-xs text-gray-400">{tx.transaction_date}</div>
                            </div>
                            <button
                              onClick={() => openDeleteConfirm(tx)}
                              className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full border border-red-200"
                              title="Delete transaction"
                              data-testid={`delete-tx-${tx.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* CSV/Excel Import Modal */}
      <Dialog open={showImportModal} onOpenChange={(open) => { if (!open) closeImportModal(); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="warranty-import-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <FileSpreadsheet className="h-5 w-5" />
              Bulk Import Warranty Transactions — Branch: {selectedBranch}
            </DialogTitle>
            <DialogDescription>
              I-upload ang CSV o Excel file para sa bulk warranty transactions. May template na pwede mong i-download para sa tamang format.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-2 space-y-4">
            {/* Step 1: Template & Upload */}
            {importPreview.length === 0 && !importResults && (
              <div className="space-y-4">
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <h3 className="font-semibold text-orange-800 mb-2">Paano gamitin:</h3>
                  <ol className="text-sm text-orange-700 space-y-1 list-decimal list-inside">
                    <li>I-download muna ang template para makita ang format.</li>
                    <li>Pun-an ang columns: <strong>transaction_type</strong> (WARRANTY_CLAIM, DEFECTIVE, REPLACEMENT, RESHIP, RETURN_TO_STOCK), <strong>item_name</strong>, <strong>quantity</strong>, <strong>location</strong> (Display Area, Stock Room, etc.).</li>
                    <li>I-upload ang Excel/CSV file at i-review ang preview bago i-confirm.</li>
                  </ol>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={downloadTemplate}
                    variant="outline"
                    className="border-blue-500 text-blue-700"
                    data-testid="download-template-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                  </Button>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="select-file-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Select CSV/Excel File
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="warranty-file-input"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Preview */}
            {importPreview.length > 0 && !importResults && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="font-semibold">{importPreview.length} rows detected</span>
                    {" — "}
                    <span className="text-green-700">{importPreview.filter(r => r.valid).length} valid</span>
                    {", "}
                    <span className="text-red-700">{importPreview.filter(r => !r.valid).length} errors</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setImportPreview([]); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                  >
                    <X className="h-4 w-4 mr-1" /> Reset
                  </Button>
                </div>

                <div className="border rounded-lg overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left">#</th>
                        <th className="px-2 py-2 text-left">Status</th>
                        <th className="px-2 py-2 text-left">Type</th>
                        <th className="px-2 py-2 text-left">Item</th>
                        <th className="px-2 py-2 text-right">Qty</th>
                        <th className="px-2 py-2 text-left">Location</th>
                        <th className="px-2 py-2 text-left">Customer</th>
                        <th className="px-2 py-2 text-left">Tracking</th>
                        <th className="px-2 py-2 text-left">Replacement</th>
                        <th className="px-2 py-2 text-left">Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.map(r => (
                        <tr key={r.row} className={r.valid ? "border-b" : "border-b bg-red-50"}>
                          <td className="px-2 py-1.5">{r.row}</td>
                          <td className="px-2 py-1.5">
                            {r.valid ? (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">OK</span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs">ERROR</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 font-medium">{r.transaction_type}</td>
                          <td className="px-2 py-1.5">{r.item_name}</td>
                          <td className="px-2 py-1.5 text-right">{r.quantity}</td>
                          <td className="px-2 py-1.5">{r.location}</td>
                          <td className="px-2 py-1.5">{r.customer_name}</td>
                          <td className="px-2 py-1.5">{r.original_tracking}</td>
                          <td className="px-2 py-1.5">{r.replacement_item_name}</td>
                          <td className="px-2 py-1.5 text-red-700 text-xs">
                            {r.errors.join("; ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {importPreview.some(r => !r.valid) && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                    <AlertTriangle className="inline h-4 w-4 mr-1" />
                    May errors — i-fix muna sa file at i-upload ulit, o magpatuloy lang sa valid rows.
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Results */}
            {importResults && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-700">{importResults.total}</div>
                    <div className="text-xs text-blue-600">Total</div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-700">{importResults.saved}</div>
                    <div className="text-xs text-green-600">Saved</div>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-red-700">{importResults.errors}</div>
                    <div className="text-xs text-red-600">Errors</div>
                  </div>
                </div>

                <div className="border rounded-lg overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left">#</th>
                        <th className="px-2 py-2 text-left">Status</th>
                        <th className="px-2 py-2 text-left">Type</th>
                        <th className="px-2 py-2 text-left">Item</th>
                        <th className="px-2 py-2 text-right">Qty</th>
                        <th className="px-2 py-2 text-left">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(importResults.results || []).map(r => (
                        <tr key={r.row} className={r.success ? "border-b" : "border-b bg-red-50"}>
                          <td className="px-2 py-1.5">{r.row}</td>
                          <td className="px-2 py-1.5">
                            {r.success ? (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">SAVED</span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs">FAILED</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5">{r.transaction_type}</td>
                          <td className="px-2 py-1.5">{r.item_name}</td>
                          <td className="px-2 py-1.5 text-right">{r.quantity}</td>
                          <td className="px-2 py-1.5 text-xs">{r.error || r.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            {importResults ? (
              <Button onClick={closeImportModal} className="bg-orange-600 hover:bg-orange-700" data-testid="close-import-results-btn">
                Close
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={closeImportModal} disabled={importing}>
                  Cancel
                </Button>
                {importPreview.length > 0 && (
                  <Button
                    onClick={handleBulkImport}
                    disabled={importing || importPreview.filter(r => r.valid).length === 0}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="confirm-bulk-import-btn"
                  >
                    {importing ? "Importing..." : `Import ${importPreview.filter(r => r.valid).length} Valid Rows`}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Delete Warranty Transaction
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="font-medium text-red-800">Are you sure you want to delete this transaction?</p>
              <p className="text-sm text-red-600 mt-1">All inventory changes will be reversed.</p>
            </div>
            {txToDelete && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="font-semibold">{txToDelete.item_name}</div>
                <div className="text-sm text-gray-600">
                  Type: {WARRANTY_TYPES.find(t => t.value === txToDelete.transaction_type)?.label || txToDelete.transaction_type} | Qty: {txToDelete.quantity}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Reason for deletion (optional)</label>
              <Input
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="e.g., Wrong entry, duplicate..."
                data-testid="delete-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              onClick={handleDelete} 
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleting ? "Deleting..." : "Delete & Reverse"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
